/**
 * Splitting a contact-list title into the part the recruiter edits and the
 * part they do not.
 *
 * The extension belongs to the STORED OBJECT, not to the title: the API reads
 * it off `s3_key` — the same source `is_json` uses — and re-applies it to
 * whatever stem is submitted (`domain.normalize_rename`). This module is the
 * client-side half of that rule, and exists so the editor can show the
 * extension as fixed text instead of letting someone delete it and be silently
 * corrected on save.
 *
 * The known set is deliberately CLOSED and matches the server's: only `.csv`
 * and `.json` are extensions here, so a title like `Acme Corp. shortlist`
 * keeps its dot instead of losing everything after it.
 */

/** Extensions this table actually stores. Mirrors `_KNOWN_EXTENSIONS` in
 *  `packages/api/app/web/v2/imports/domain.py`. */
const KNOWN_EXTENSIONS = [".csv", ".json"] as const;

/** Longest editable stem, in UTF-8 BYTES. Mirrors `MAX_IMPORT_NAME_STEM_BYTES`
 *  on the server, which is the authority — this only spares the recruiter a
 *  round-trip. Bytes rather than characters because 255 is a byte limit
 *  everywhere it is enforced; for an ASCII title the two coincide, which is
 *  exactly why counting characters looks correct until someone types in
 *  Spanish or Japanese. */
export const MAX_TITLE_STEM_BYTES = 250;

/** Characters that must never reach the stored title: they would break the
 *  `Content-Disposition` header the download presign builds from it. Matching
 *  control characters IS the intent here, so the rule is suppressed rather
 *  than worked around — a title carrying a CR/LF must be caught, not
 *  quietly accepted because the linter dislikes the range. */
// biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting them is the point
const HEADER_HOSTILE = /["/\\]|[\u0000-\u001F\u007F]/;

/**
 * True when `character` is a one-to-one compatibility variant of a hostile one.
 *
 * Mirrors `_is_forbidden_variant` in `packages/api/app/web/v2/imports/domain.py`.
 * `＂` (U+FF02) is not `"` until something NFKD-normalizes it, and then it is.
 * There are exactly four such characters in Unicode — `＂ ／ ＼ ﹨` — and they
 * are all CJK-width forms, so a client that misses this rule refuses nothing
 * and lets the server 400 precisely the recruiters the rule exists for.
 *
 * One-to-one is load-bearing: `℅` decomposes to the three characters `c/o` and
 * is a legitimate care-of sign, not a disguised slash. Same carve-out as the
 * server's, for the same reason.
 */
function isHostileVariant(character: string): boolean {
  const decomposed = character.normalize("NFKD");
  return decomposed.length === 1 && HEADER_HOSTILE.test(decomposed);
}

export interface SplitTitle {
  /** The editable part. The whole title when there is no known extension. */
  stem: string;
  /** `".json"`, or `""` when the title carries no known extension. */
  extension: string;
}

/**
 * The title as the recruiter should ever SEE it: the stem, without the stored
 * extension.
 *
 * Display only. The extension is never dropped from anything that is stored,
 * sent, or downloaded — `imports.original_filename` keeps it, the rename
 * endpoint re-applies it, and `Content-Disposition` is built from it. The rule
 * is: a recruiter named their list, and `.csv` is a fact about the file we
 * happen to hold, not part of the name they chose.
 *
 * **Applies to every surface that PRINTS the name of a contact list** — the
 * detail heading, the firm-wide table, the import picker, the selection-bar
 * summary, the Full history panel, and prose that names a list (e.g. the AI
 * Review confirm dialog).
 *
 * It does NOT apply to an EDITABLE field whose value is the filename itself.
 * `AiReviewPage`'s output-name input keeps the extension visible, because there
 * the value is the thing being authored and the extension decides the file's
 * format — hiding it would take a functional choice away from the recruiter
 * rather than tidying a label. The distinction is print-vs-author, not
 * page-by-page (`/pr-review` iter 4, Agent C I7).
 *
 * Uses the same CLOSED extension set as the editor, so `Acme Corp. shortlist`
 * keeps its dot instead of losing everything after it.
 */
export function displayTitle(filename: string | null | undefined): string {
  return filename ? splitTitle(filename).stem : "";
}

/** Split a stored title into `{ stem, extension }`. */
export function splitTitle(filename: string): SplitTitle {
  const lowered = filename.toLowerCase();
  for (const extension of KNOWN_EXTENSIONS) {
    if (lowered.endsWith(extension)) {
      return {
        stem: filename.slice(0, -extension.length),
        // Sliced from the ORIGINAL, not the lowercased probe — a `.JSON`
        // upload keeps the casing it was stored with.
        extension: filename.slice(-extension.length),
      };
    }
  }
  return { stem: filename, extension: "" };
}

/**
 * Why this stem cannot be saved, or `null` when it can.
 *
 * The server validates independently and is the authority; this exists so Save
 * can be disabled with a reason rather than firing a request already known to
 * 400. The two rule sets are kept identical on purpose — a client that permits
 * what the server rejects teaches recruiters to distrust the form.
 */
export function titleError(stem: string): string | null {
  const trimmed = stem.trim();
  if (!trimmed.replace(/\./g, "")) return "Enter a title.";
  const bytes = new TextEncoder().encode(trimmed).length;
  if (bytes > MAX_TITLE_STEM_BYTES)
    return `Too long (${bytes} of ${MAX_TITLE_STEM_BYTES} bytes). Accented and non-Latin characters take more than one byte each.`;
  if (HEADER_HOSTILE.test(trimmed) || [...trimmed].some(isHostileVariant))
    return "Titles cannot contain slashes, quotes or line breaks.";
  return null;
}
